import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { ChildProfileForm } from '../../components/auth';
import { useAuth } from '../../contexts/AuthContext';
import { logger } from '../../services/utils';
import './ProfileView.css';

/**
 * User profile page displaying account information and child profiles
 */
const ProfileView = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  
  const { 
    user, 
    userProfile, 
    childProfiles, 
    activeChildProfile,
    setActiveChildProfile,
    logout, 
    error: authError 
  } = useAuth();
  
  const navigate = useNavigate();
  
  // Log user data for debugging
  useEffect(() => {
    if (user) {
      logger.debug('ProfileView', 'User data', user);
    }
  }, [user]);
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      setError('Failed to log out. Please try again.');
    }
  };
  
  const handleSetActiveProfile = (profile) => {
    setActiveChildProfile(profile);
    setSuccessMessage(`Switched to ${profile.name}'s profile`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  
  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    setSuccessMessage('Child profile created successfully!');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  
  if (!user) {
    return <div>Loading...</div>;
  }
  
  return (
    <div className="profile-view">
      <div className="profile-view__header">
        <h1 className="profile-view__title">Your Profile</h1>
        <p className="profile-view__subtitle">Manage your account and child profiles</p>
      </div>
      
      {/* Success message */}
      {successMessage && (
        <div className="profile-view__message profile-view__message--success">
          {successMessage}
        </div>
      )}
      
      {/* Error message */}
      {(error || authError) && (
        <div className="profile-view__message profile-view__message--error">
          {error || authError}
        </div>
      )}
      
      <div className="profile-view__section">
        <h2 className="profile-view__section-title">Account Information</h2>
        <Card className="profile-view__section-content">
          <div className="profile-view__user-info">
            <div className="profile-view__user-info-item">
              <span className="profile-view__user-info-label">Email:</span>
              <span className="profile-view__user-info-value">{user.email}</span>
            </div>
            <div className="profile-view__user-info-item">
              <span className="profile-view__user-info-label">Account Created:</span>
              <span className="profile-view__user-info-value">
                {user.date_joined ? new Date(user.date_joined).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
          
          <div className="profile-view__actions">
            <Button 
              variant="outlined" 
              onClick={() => navigate('/accounts/password/change/')}
            >
              Change Password
            </Button>
          </div>
        </Card>
      </div>
      
      <div className="profile-view__section">
        <h2 className="profile-view__section-title">Child Profiles</h2>
        <Card className="profile-view__section-content">
          {childProfiles.length === 0 ? (
            <p>You haven't created any child profiles yet.</p>
          ) : (
            childProfiles.map(profile => (
              <div key={profile.id} className="profile-view__child-profile">
                <div className="profile-view__child-profile-info">
                  <span className="profile-view__child-profile-name">
                    {profile.name}
                    {activeChildProfile?.id === profile.id && ' (Active)'}
                  </span>
                  {profile.age && (
                    <span className="profile-view__child-profile-age">Age: {profile.age}</span>
                  )}
                </div>
                <div className="profile-view__child-profile-actions">
                  {activeChildProfile?.id !== profile.id ? (
                    <Button 
                      variant="outlined" 
                      className="profile-view__switch-button"
                      onClick={() => handleSetActiveProfile(profile)}
                    >
                      Switch to
                    </Button>
                  ) : (
                    <Button 
                      variant="outlined" 
                      onClick={() => navigate(`/weaversworld`)}
                    >
                      Stories
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
          
          {/* Add Child Profile button removed for initial launch */}
        </Card>
      </div>
      
      <Button
        variant="outlined"
        className="profile-view__logout-button"
        onClick={handleLogout}
      >
        Sign Out
      </Button>
      
      {/* Child Profile Creation Modal */}
      {showCreateForm && (
        <div className="profile-view__modal">
          <div className="profile-view__modal-content">
            <ChildProfileForm
              onSuccess={handleCreateSuccess}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileView;